import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { getQueryFromUrl } from '~commonInfra/url/UrlHelpers';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { Helpers } from '~commonInfra/utils/Helpers';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { IDataSourceDetailInfo } from '~src/datasource/models/IDataSourceInfo';
import { RefreshOptionType } from '~src/datasource/resources/Datasources';
import { IGetPreferredRefreshOptionResult } from '~src/datasource/ServerApi/requests/GetPreferredRefreshOption';
import { RefreshModeTypes } from '~src/extractRefresh/serverApi/types/IRemoteRefreshProperties';
import { selectContentCounts, selectContentCountsUpToDate } from '~src/spacesPlatform/selectors/ContentCountsSelector';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { PublishDatasourceAction } from '~src/uploadContent/actions/PublishDatasourceAction';

export interface IDatasourcePublishResult extends ISyncPublishResult {
  preferredRefreshOption: IGetPreferredRefreshOptionResult;
}

export interface ISyncPublishResult {
  isSyncAvailable: boolean;
  isSyncRunning: boolean;
  remoteAgentWorkflowId: string;
  publishedWithLQS: boolean;
}

// Ported from the old DatasourcePublishResultCtrl.ts pre-2021.3
export namespace DatasourcePublishResultService {

  export function handlePublishedDatasource(datasourceDetailInfo: IDataSourceDetailInfo,
    preferredRefreshOption: IGetPreferredRefreshOptionResult,
    resultUrl: string) {

    const publishResult: IDatasourcePublishResult = {
      preferredRefreshOption: null,
      isSyncAvailable: null,
      isSyncRunning: null,
      remoteAgentWorkflowId: null,
      publishedWithLQS: null
    };

    publishResult.preferredRefreshOption = preferredRefreshOption;

    let search = getQueryFromUrl(resultUrl);

    switch (preferredRefreshOption.suggestion) {
      case RefreshOptionType.OnlineSync:
        // Before we go, pass along the sync-related query string parameters
        if (_.has(search, UrlParamKeys.SyncAvailable)) {
          publishResult.isSyncAvailable = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncAvailable]);
        }
        if (_.has(search, UrlParamKeys.SyncRunning)) {
          publishResult.isSyncRunning = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.SyncRunning]);
        }
        if (_.has(search, UrlParamKeys.RemoteAgentWorkflowId)) {
          publishResult.remoteAgentWorkflowId = search[UrlParamKeys.RemoteAgentWorkflowId];
        }

        showSuccessDialogAfterRedirect(publishResult, datasourceDetailInfo);
        break;

      case RefreshOptionType.LiveConnection:
        if (_.has(search, UrlParamKeys.PublishedWithLQS)) {
          publishResult.publishedWithLQS = Helpers.BooleanQueryParamRegex.test(search[UrlParamKeys.PublishedWithLQS]);
        }

        showSuccessDialogAfterRedirect(publishResult, datasourceDetailInfo);

        break;
      case RefreshOptionType.NotSupported:
      case RefreshOptionType.FeatureDisabled:
        showSuccessToast(datasourceDetailInfo);
        break;
      case RefreshOptionType.Server:
        showSuccessDialogAfterRedirect(publishResult, datasourceDetailInfo);
      default:
        break;
    }

    // don't need to redirect here after publishing because the DatasourceSpaceRouter handles that- tfs944083
  }

  function showSuccessDialogAfterRedirect(publishResult: IDatasourcePublishResult, datasourceDetailInfo: IDataSourceDetailInfo) {
    // If we open the modal now and then initiate a state change, the modal will not open properly.
    // So, we'll wait for the state to finish changing and then open the modal
    if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.Server || publishResult.preferredRefreshOption.suggestion === RefreshOptionType.OnlineSync) {
        // We need to get the existing scheduled refreshes count for this data source
        // Wait until the content counts are loaded in the redux store.
        const handlePublishDatasourceAfterGettingCounts = () => {
          const appState = getAppStore().getState();
          const countsUpToDate = selectContentCountsUpToDate(appState);
          const contentCounts: ISpaceInfoTypeItemCount<'datasource'> = selectContentCounts<'datasource'>(appState, 'datasource');
          if (countsUpToDate) {
            if (datasourceDetailInfo.refreshMode !== RefreshModeTypes.Remote) {
              PublishDatasourceAction.handlePublishedDatasource(contentCounts['extractRefreshes'], publishResult, datasourceDetailInfo);
            } else {
              PublishDatasourceAction.handlePublishedDatasource(contentCounts['remoteRefreshSchedules'], publishResult, datasourceDetailInfo);
            }
          } else {
            setTimeout(handlePublishDatasourceAfterGettingCounts, 250);
          }
        };
        handlePublishDatasourceAfterGettingCounts();
      } else {
        // For live refresh option
        PublishDatasourceAction.handlePublishedDatasource(0, publishResult, datasourceDetailInfo);
      }
  }

  function showSuccessToast(datasourceDetailInfo: IDataSourceDetailInfo) {
    ToasterService.instance.pop({
      type: ToastType.Success,
      text: Messages.datasourcePublishSuccess({ DATASOURCE_NAME: L10n.elideIfLong(datasourceDetailInfo.name) })
    });
  }
}

export type DatasourcePublishResultService = typeof DatasourcePublishResultService;
